import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons/faTriangleExclamation";
import { Switch } from "../../../utils/local-antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Info } from "../../../components/Info";
import { Text } from "../../../components/Text";
import { Modal } from "../../../components/modal/Modal";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { standardDate } from "../../../utils/Date";
import { useDrawersContext } from "../../tasks/drawers-context";
import { entryActions } from "../../../redux/entry/entry.slice";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import omitBy from "lodash/omitBy";
import isNil from "lodash/isNil";
import { useFormContext } from "react-hook-form";
import { add } from "date-fns";
import { TestIDs } from "../../../tests/types/testIds";

const DenyButton = styled(Button)`
  background-color: ${({ theme }) => theme.white};
  border: 1px solid ${({ theme }) => theme.black};
  color: ${({ theme }) => theme.black};

  &:hover {
    background-color: ${({ theme }) => theme.black};
    color: ${({ theme }) => theme.white};
  }
`;

const StyledWarningModal = styled(Modal)`
  z-index: 1000000;
`;

export const EntryDeleteModal: React.FC = () => {
  const [isInstantDelete, setIsInstantDelete] = useState(false);

  const dispatch = useAppDispatch();
  const { t } = useTranslation(undefined, { keyPrefix: "entries.delete" });
  const { getValues } = useFormContext();
  const {
    entry,
    selectedRows: selectedEntriesIds,
    setSelectedRows: setSelectedEntriesIds,
    entryDeleteLoading,
    isBulkEntryAction,
    entryDeleteVisible,
    setEntryDeleteVisible,
    setEntryDeleteLoading,
    setIsBulkEntryAction,
  } = useDrawersContext();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const entriesPage = useAppSelector(
    entrySliceSelectors.selectEntriesTablePage,
  );
  const entriesPageSize = useAppSelector(
    entrySliceSelectors.selectEntriesTablePageSize,
  );
  const isMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);

  const localOnClose = () => {
    setEntryDeleteVisible(false);
    setIsInstantDelete(false);
  };

  const deleteEntry = async () => {
    try {
      setEntryDeleteLoading(true);
      await dispatch(
        entryActions.deleteEntry({
          userId: userId,
          entryId: entry?._id!,
          instantDelete:
            entry?.markedForDeletion === true ? true : isInstantDelete,
        }),
      ).unwrap();

      setEntryDeleteVisible(false);

      await dispatch(
        entryActions.getLatestEntries({
          userId,
          meta: {
            count: entriesPageSize,
            page: entriesPage - 1,
          },
        }),
      ).unwrap();

      localOnClose();

      AtiraToast.success(t("success"));
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setEntryDeleteLoading(false);
    }
  };

  const bulkDelete = async () => {
    try {
      setEntryDeleteLoading(true);

      await dispatch(
        entryActions.deleteEntryBulk({
          instantDelete: isInstantDelete,
          userId,
          entriesIds: selectedEntriesIds as string[],
        }),
      ).unwrap();
      await dispatch(
        entryActions.getLatestEntries({
          userId,
          meta: { count: 10, page: 0 },
          ...omitBy(getValues(), isNil),
        }),
      ).unwrap();

      localOnClose();

      setSelectedEntriesIds([]);
      setIsBulkEntryAction(false);
      AtiraToast.success(
        t("bulkHeader.delete.success", {
          entries_count: selectedEntriesIds.length,
        }),
      );
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setEntryDeleteLoading(false);
    }
  };

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  return (
    <StyledWarningModal
      destroyOnHidden
      open={entryDeleteVisible}
      onClose={localOnClose}
      title={t(isBulkEntryAction ? "modal.bulk.title" : "modal.title")}
    >
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="xl"
      >
        <Flex flexDirection="column" gap="m" alignItems="center">
          <Flex width={"4rem"}>
            <AtiraIcon icon={faTriangleExclamation} size="4x" color="main" />
          </Flex>

          <Text
            fontSize="l"
            align="center"
            fontWeight={"bold"}
            color="darkerSub"
          >
            {t(isBulkEntryAction ? "modal.bulk.subtitle" : "modal.subtitle")}
          </Text>

          <Info
            info={
              entry?.markedForDeletionAt
                ? t("modal.marked_to_deletion.description", {
                    date: standardDate(
                      add(new Date(entry?.markedForDeletionAt), { days: 30 }),
                      user,
                    ),
                  })
                : t(
                    isBulkEntryAction
                      ? "modal.bulk.description"
                      : "modal.description",
                  )
            }
            color="warning"
          />
        </Flex>

        {!entry?.markedForDeletionAt && !isMember ? (
          <Flex
            flexDirection="column"
            gap="s"
            width={"100%"}
            alignItems="center"
          >
            <Flex gap="s" alignItems="center" marginBottom="s">
              <Text>
                {t(
                  isBulkEntryAction
                    ? "modal.bulk.switch.title"
                    : "modal.switch.title",
                )}
              </Text>

              <Switch
                onChange={setIsInstantDelete}
                data-testid={TestIDs.entries.table.menu.delete.modal.switch}
              />
            </Flex>
            {isInstantDelete ? (
              <Info
                info={t(
                  isBulkEntryAction
                    ? "modal.bulk.switch.description"
                    : "modal.switch.description",
                )}
                color="danger"
                icon={faTriangleExclamation}
              />
            ) : null}
          </Flex>
        ) : null}

        <Flex gap="m">
          <DenyButton onClick={localOnClose}>{t("modal.button.no")}</DenyButton>

          <Button
            hover
            loading={entryDeleteLoading}
            onClick={!isBulkEntryAction ? deleteEntry : bulkDelete}
            dataTestId={
              isBulkEntryAction
                ? ""
                : TestIDs.entries.table.menu.delete.modal.button
            }
          >
            {t("modal.button.yes")}
          </Button>
        </Flex>
      </Flex>
    </StyledWarningModal>
  );
};
